import { useState } from 'react';

const WIDTH = 120;
const HEIGHT = 28;

/**
 * The last minute of one policy's speed: a single 2px line in the primary token over a
 * faint area, scaled from zero to the larger of the limit and the peak, with the limit
 * as a dashed rule. The service keeps the minute, `stepS` apart and oldest first, so
 * the line is whole the moment the page opens. Hovering reads out the nearest point.
 */
export function Sparkline(props: { readonly values: ReadonlyArray<number>; readonly stepS: number; readonly limit: number; readonly label: string }) {
  const [hover, setHover] = useState<number | undefined>(undefined);
  const samples = props.values.map((requestsPerSecond, index) => ({ requestsPerSecond, agoS: (props.values.length - index) * props.stepS }));
  if (samples.length < 2) {
    return <div className="h-7 w-32 rounded bg-muted/50" aria-label={`${props.label}: no samples`} />;
  }
  const top = Math.max(props.limit, ...samples.map((sample) => sample.requestsPerSecond)) * 1.1 || 1;
  const x = (index: number) => (index / (samples.length - 1)) * WIDTH;
  const y = (value: number) => HEIGHT - (value / top) * (HEIGHT - 2) - 1;
  const line = samples.map((sample, index) => `${x(index).toFixed(1)},${y(sample.requestsPerSecond).toFixed(1)}`).join(' ');
  const hovered = hover === undefined ? undefined : samples[hover];

  return (
    <div className="relative h-7 w-32">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="size-full overflow-visible"
        role="img"
        aria-label={`${props.label}: ${samples[samples.length - 1].requestsPerSecond.toFixed(2)} requests per second in the last ${props.stepS} seconds, limit ${props.limit.toFixed(2)}`}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          setHover(Math.round(((event.clientX - box.left) / box.width) * (samples.length - 1)));
        }}
        onMouseLeave={() => setHover(undefined)}
      >
        <line x1={0} x2={WIDTH} y1={y(props.limit)} y2={y(props.limit)} className="stroke-muted-foreground/40" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        <polygon points={`0,${HEIGHT} ${line} ${WIDTH},${HEIGHT}`} className="fill-primary/15" />
        <polyline points={line} fill="none" className="stroke-primary" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {hovered === undefined || hover === undefined ? null : (
          <line x1={x(hover)} x2={x(hover)} y1={0} y2={HEIGHT} className="stroke-foreground/50" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      {hovered === undefined ? null : (
        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow">
          {hovered.requestsPerSecond.toFixed(2)}/s · {hovered.agoS - props.stepS}–{hovered.agoS}s ago
        </div>
      )}
    </div>
  );
}
